import * as formik from "formik";
import { useContext, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { AlertType } from "../../../types/AlertMessage";
import { CurrentUser } from "../../../types/User";
import { useAlertContext } from "../../alert/AlertContext";
import { AuthContext } from "../../auth/AuthContext";
import { useAccountService } from "../../../hooks";
import PasswordInput from "../../../@ui/PasswordInput/PasswordInput";
import {
  EndCol,
  FooterCol,
  FooterLayout,
} from "../../../@ui/ModalFooterLayout";
import { PendingLicencePurchaseSummary } from "../../../types/PendingLicencePurchase/PendingLicencePurchaseSummary";
import {
  clearPendingPremiumOrder,
  getPendingPremiumOrder,
} from "../../../utils/pendingPremiumOrder";
import RenewalMessage from "./RenewalMessage";

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alertContext = useAlertContext();
  const authContext = useContext(AuthContext);
  const accountService = useAccountService();
  const [badCredentials, setBadCredentials] = useState();
  const [badToken, setBadToken] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);

  const redirectParam = searchParams.get("redirect") ?? "";

  const { Formik } = formik;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    let refreshToken = urlParams.get("refreshToken");
    if (token && refreshToken) {
      setLoadingToken(true);
      refreshToken = atob(refreshToken);
      accountService
        .LoginSso(token, refreshToken)
        .then((res) => {
          let userDetails: CurrentUser = {
            username: res.user.username,
            id: res.user.id,
            firstName: res.user.firstName,
            lastName: res.user.lastName,
            roles: res.user.roles.map((r: any) => r.name),
            token: token,
            refreshToken: res.refreshToken,
            tokenExpiry: res.tokenExpiry,
          };
          authContext?.login(userDetails);

          return;
        })
        .catch(() => {
          setBadToken(true);
          alertContext.showAlert({
            message: "Unable to login from SSO, please try manually",
            type: AlertType.Error,
          });
        })
        .finally(() => {
          setLoadingToken(false);
        });

      // Remove the token from the query params
      urlParams.delete("token");
    }

    if (authContext?.user) {
      navigate("/home");
    }
  }, []);

  const schema = yup.object({
    Username: yup.string().required(),
    Password: yup.string().required(),
  });

  const handleLoginSubmit = (values: any) => {
    setLoadingLogin(true);
    accountService
      .Login(values.Username, values.Password)
      .then((r) => {
        setBadCredentials(undefined);
        authContext?.login({
          username: r.user.username,
          firstName: r.user.firstName,
          lastName: r.user.lastName,
          id: r.user.id,
          token: r.token,
          roles: r.user.roles.map((r) => r.name),
          tokenExpiry: r.tokenExpiry,
          refreshToken: r.refreshToken,
        });

        alertContext.showAlert({
          message: "Login successful",
          type: AlertType.Information,
        });

        // Check for pending premium order
        const pendingOrder = getPendingPremiumOrder();
        if (pendingOrder) {
          clearPendingPremiumOrder();
          navigate(pendingOrder.redirectPath);
        } else {
          let redirect = "/home";
          if (searchParams.has("redirect")) {
            const redirectParam = searchParams.get("redirect")!;
            if (!redirectParam.toLowerCase().startsWith("/maintenance"))
              redirect = redirectParam;
          }
          navigate(redirect);
        }
      })
      .catch((ex) => {
        if (ex.response.status === 402) {
          const licenceSummary = ex.response
            .data as PendingLicencePurchaseSummary;

          navigate("/purchase-licence", {
            state: { id: licenceSummary.id, hash: licenceSummary.hash },
          });
        }
        setBadCredentials(ex.response.data);
      })
      .finally(() => {
        setLoadingLogin(false);
      });
  };

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={handleLoginSubmit}
        initialValues={{ Username: "", Password: "" }}
      >
        {({
          handleChange,
          values,
          errors,
          handleBlur,
          touched,
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit} className="d-flex flex-column h-100">
            <Modal.Header className="bg-secondary text-white">
              <Modal.Title>Login</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <RenewalMessage redirectParam={redirectParam} />
              {!loadingToken && (
                <>
                  {badToken && (
                    <p className="text text-danger">
                      Unable to login automatically from SSO. Please try
                      manually below.
                    </p>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label>Username / Email</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter username"
                      onChange={handleChange}
                      value={values.Username}
                      isInvalid={touched.Username && !!errors.Username}
                      onBlur={handleBlur}
                      name="Username"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Username}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <PasswordInput
                      onChange={handleChange}
                      value={values.Password}
                      isInvalid={touched.Password && !!errors.Password}
                      onBlur={handleBlur}
                      name="Password"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Password}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Button
                    variant="link"
                    onClick={() => {
                      navigate("/forgottenPassword");
                    }}
                  >
                    Forgotten Password
                  </Button>
                  {badCredentials && (
                    <>
                      <Form.Text className="text-danger">
                        {badCredentials}
                      </Form.Text>
                      <br />
                    </>
                  )}
                </>
              )}
              {loadingToken && (
                <>
                  <p>Logging you into DataCutter from SSO...</p>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <FooterLayout>
                <FooterCol>
                  <Button
                    variant="link"
                    onClick={() => {
                      navigate("/register");
                    }}
                  >
                    Don't have an account?
                  </Button>
                </FooterCol>
                <EndCol>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loadingLogin}
                  >
                    Login {loadingLogin && <Spinner size="sm" />}
                  </Button>
                </EndCol>
              </FooterLayout>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </>
  );
};
